import type {
  KeplerianElements,
  OrbitMeta,
  OrbitPointStatus,
  OrbitStatusMeta,
  OrbitType,
  PhysicalCharacteristics,
  Satellite,
  SatGroup,
  TreeSatItem,
} from '../types/mission';
import type { SensorData } from '../types/sensor';

function epochToDisplay(sec: number): string {
  if (!sec) return '';
  const d = new Date(sec * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

function toOrbitType(raw: string): OrbitType {
  const map: Record<string, OrbitType> = { Inclined: 'inclined', SSO: 'sso', GEO: 'geo', HEO: 'heo' };
  return map[raw] ?? 'inclined';
}

function getSunRtAscension(epochDate: Date): number {
  const j2000 = Date.UTC(2000, 0, 1, 12, 0, 0);
  const diffDays = (epochDate.getTime() - j2000) / 86400000;
  let L = (280.460 + 0.9856474 * diffDays) % 360;
  if (L < 0) L += 360;
  let g = (357.528 + 0.9856003 * diffDays) % 360;
  if (g < 0) g += 360;
  const g_rad = g * Math.PI / 180;
  const lambda = L + 1.915 * Math.sin(g_rad) + 0.020 * Math.sin(2 * g_rad);
  const lambda_rad = lambda * Math.PI / 180;
  const epsilon_rad = 23.439 * Math.PI / 180;
  const ra_rad = Math.atan2(Math.cos(epsilon_rad) * Math.sin(lambda_rad), Math.cos(lambda_rad));
  let ra_deg = ra_rad * 180 / Math.PI;
  if (ra_deg < 0) ra_deg += 360;
  return ra_deg;
}

function ltanToRaan(ltan: string, epoch: string, epochTz: 'UTC' | 'IST' = 'UTC'): number {
  const suffix = epochTz === 'IST' ? '+05:30' : 'Z';
  const epochDate = new Date(epoch.replace(' ', 'T') + suffix);
  if (isNaN(epochDate.getTime())) return 0;
  const sunRaDeg = getSunRtAscension(epochDate);
  const parts = ltan.split(':').map(Number);
  const ltanHours = (parts[0] || 0) + (parts[1] || 0) / 60 + (parts[2] || 0) / 3600;
  return ((ltanHours - 12) * 15 + sunRaDeg + 720) % 360;
}

function toOrbitElements(op: any, orbitType?: OrbitType, ltan?: string, epochDisplay?: string): KeplerianElements {
  let RAAN = op.RAAN ?? 0;
  if (orbitType === 'sso' && ltan && epochDisplay) {
    RAAN = ltanToRaan(ltan, epochDisplay, 'UTC');
  }
  return {
    semiMajorAxis: op.semiMajorAxis ?? 0,
    eccentricity:  op.eccentricity  ?? 0,
    inclination:   op.inclination   ?? 0,
    RAAN:          RAAN,
    AOP:           op.AOP           ?? 0,
    TA:            op.TA            ?? 0,
    epoch:         op.epoch         ?? 0,
  };
}

function toOrbitMeta(item: any): OrbitMeta {
  const ref = item.referenceOrbit ?? {};
  const type = toOrbitType(item.orbitType ?? 'Inclined');
  const inputType = String(item.orbitalElements ?? '').toLowerCase();
  const normalizedInputType = ['keplerian', 'cartesian', 'equinoctial'].includes(inputType)
    ? (inputType as 'keplerian' | 'cartesian' | 'equinoctial')
    : (item.referenceOrbit?.position ? 'cartesian' : 'keplerian');

  const base: OrbitMeta = {
    orbitType:       type,
    orbitTypeLabel:  item.orbitType ?? 'Inclined',
    epoch:           epochToDisplay(ref.epoch),
    refFrame:        item.referenceFrame ?? 'EME2000',
    altitude_km:     ref.altitude      ?? 0,
    eccentricity:    ref.eccentricity  ?? 0,
    inclination_deg: ref.inclination   ?? 0,
    raan_deg:        ref.RAAN          ?? 0,
    aop_deg:         ref.AOP           ?? 0,
    trueAnomaly_deg: ref.TA            ?? 0,
    inputType:       normalizedInputType,
  };

  if (normalizedInputType === 'cartesian') {
    if (Array.isArray(ref.position)) {
      base.position = [ref.position[0] ?? 0, ref.position[1] ?? 0, ref.position[2] ?? 0];
    }
    if (Array.isArray(ref.velocity)) {
      base.velocity = [ref.velocity[0] ?? 0, ref.velocity[1] ?? 0, ref.velocity[2] ?? 0];
    }
  }

  if (normalizedInputType === 'equinoctial') {
    if (Array.isArray(ref.eccentricityVector)) {
      base.eccentricityVector = [ref.eccentricityVector[0] ?? 0, ref.eccentricityVector[1] ?? 0];
    }
    if (Array.isArray(ref.inclinationVector)) {
      base.inclinationVector = [ref.inclinationVector[0] ?? 0, ref.inclinationVector[1] ?? 0];
    }
    base.longitude_deg = ref.longitude ?? 0;
  }

  if (type === 'sso') base.ltan = ref.LTAN ?? undefined;
  if (type === 'geo') base.longitude_deg = ref.longitude ?? 0;
  if (type === 'heo') {
    base.perigee_km = ref.perigeeDistance ?? 0;
    base.apogee_km = ref.apogeeDistance ?? 0;
  }

  return base;
}

function toSensorData(s: any): SensorData {
  const geo = s.geometry ?? {};
  const pa  = s.pointingAxis ?? {};
  const lc  = s.lightingCondition ?? {};

  const modeMap: Record<string, SensorData['pointingMode']> = {
    'az-ele': 'az', 'body-axis': 'dcm', euler: 'euler', nadir: 'nadir',
  };

  const lighting = (['sunlit', 'eclipse', 'umbra', 'penumbra'] as const).filter(k => lc[k]);

  return {
    sensorId:     s.sensorId ?? '',
    name:         s.name ?? '',
    geometry:     geo.type ?? 'conical',
    fov:          String(geo.conicalFov ?? 30),
    fovH:         String(geo.HFOV ?? 30),
    fovV:         String(geo.VFOV ?? 30),
    pointingMode: modeMap[pa.mode] ?? 'nadir',
    azimuth:      String(pa.azEle?.azimuth   ?? 0),
    elevation:    String(pa.azEle?.elevation ?? 0),
    yaw:          String(pa.euler?.yaw   ?? 0),
    pitch:        String(pa.euler?.pitch ?? 0),
    roll:         String(pa.euler?.roll  ?? 0),
    dcmX:         String(pa.bodyAxis?.x ?? 0),
    dcmY:         String(pa.bodyAxis?.y ?? 0),
    dcmZ:         String(pa.bodyAxis?.z ?? -1),
    lighting,
    bodyAxis:     pa.bodyAxis ? [pa.bodyAxis.x, pa.bodyAxis.y, pa.bodyAxis.z] : [0, 0, -1],
    agility:      s.agility ?? 30,
  };
}

function toPhysicalCharacteristics(pc: any): PhysicalCharacteristics {
  return {
    mass:     pc.mass     ?? pc.mass_kg ?? 0,
    volume:   pc.volume   ?? pc.volume_m3 ?? null,
    cd:       pc.cd       ?? pc.dragCoefficient ?? 2.2,
    dragArea: pc.dragArea ?? pc.dragSurfaceArea_m2 ?? 0.01,
    cr:       pc.cr       ?? pc.reflectCoefficient ?? 1.5,
    srpArea:  pc.srpArea  ?? pc.srpSurfaceArea_m2 ?? 0.04,
    inertia: {
      Ixx: pc.inertia?.Ixx ?? 0,
      Iyy: pc.inertia?.Iyy ?? 0,
      Izz: pc.inertia?.Izz ?? 0,
      Ixy: pc.inertia?.Ixy ?? 0,
      Ixz: pc.inertia?.Ixz ?? 0,
      Iyz: pc.inertia?.Iyz ?? 0,
    },
    orientation: {
      Roll:  pc.orientation?.Roll  ?? 0,
      Pitch: pc.orientation?.Pitch ?? 0,
      Yaw:   pc.orientation?.Yaw   ?? 0,
    },
  };
}

function toEphemeris(raw: any): any[] | undefined {
  const src = raw?.ephemeris ?? raw?.propagatedEphemeris ?? raw?.states ?? raw?.stateVectors;
  if (!Array.isArray(src)) return undefined;
  return src.map((p: any) => ({
    epoch: p.epoch ?? p.time ?? p.timestamp ?? 0,
    x: p.x ?? p.position?.x ?? p.r?.[0] ?? 0,
    y: p.y ?? p.position?.y ?? p.r?.[1] ?? 0,
    z: p.z ?? p.position?.z ?? p.r?.[2] ?? 0,
    vx: p.vx ?? p.velocity?.x ?? p.v?.[0],
    vy: p.vy ?? p.velocity?.y ?? p.v?.[1],
    vz: p.vz ?? p.velocity?.z ?? p.v?.[2],
  })).filter((p: any) => p.epoch && Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y)) && Number.isFinite(Number(p.z)));
}

function resolvePropagationStatus(raw: any, ephemeris?: any[]): Satellite['propagationStatus'] {
  if (ephemeris?.length) return 'ready';
  const status = normalizeOrbitStatus(raw?.orbitPointStatus ?? raw?.orbitStatus ?? raw?.propagationStatus);
  if (status === 'completed') return 'ready';
  if (status === 'failed') return 'failed';
  if (status === 'pending' || status === 'in_progress') return 'propagating';
  if (raw?.propagationStatus && ['preview', 'propagating', 'ready', 'failed'].includes(raw.propagationStatus)) {
    return raw.propagationStatus;
  }
  return 'preview';
}

function getBackendGroupId(item: any): string | undefined {
  return item?.groupId ?? item?.groupid ?? item?.group_id ?? item?.id ?? item?._id;
}

function normalizeOrbitStatus(raw: any): OrbitPointStatus | undefined {
  const status = typeof raw === 'string' ? raw : raw?.status ?? raw?.orbitPointStatus;
  const normalized = String(status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (['pending', 'in_progress', 'completed', 'failed'].includes(normalized)) {
    return normalized as OrbitPointStatus;
  }
  return undefined;
}

function toOrbitStatusMeta(raw: any, fallback: Partial<OrbitStatusMeta> = {}): OrbitStatusMeta | undefined {
  const source = raw?.orbitPointStatus ?? raw?.orbitStatus ?? raw?.propagationEventStatus ?? raw;
  const status = normalizeOrbitStatus(source);
  if (!status) return undefined;

  const objectSource = typeof source === 'object' ? source : raw;
  const total = objectSource?.satellitesTotal ?? fallback.satellitesTotal;
  const rawDone = objectSource?.satellitesDone ?? fallback.satellitesDone;
  const done = status === 'completed' && total != null ? total : rawDone;

  return {
    ...fallback,
    status,
    runId: objectSource?.runId ?? fallback.runId,
    groupId: objectSource?.groupId ?? objectSource?.groupid ?? fallback.groupId,
    satelliteId: objectSource?.satelliteId ?? objectSource?.satid ?? fallback.satelliteId,
    satellitesTotal: total,
    satellitesDone: done,
    error: objectSource?.error ?? fallback.error,
    updatedAt: objectSource?.updatedAt ?? fallback.updatedAt,
  };
}

function mapApiSatChild(sat: any, orbitMeta: OrbitMeta, groupId?: string): Satellite {
  const config = sat.config ?? {};
  const physicalCharacteristics = config.physicalCharacteristics ?? sat.physicalCharacteristics;
  const ephemeris = toEphemeris(sat);

  let tleMeta: any = undefined;

  if (sat.tleLines) {
    tleMeta = {
      source: 'custom_tle',
      line1: sat.tleLines.line1,
      line2: sat.tleLines.line2,
    };
  } else if (sat.tle) {
    const tleLines = sat.tle.split('\n').filter((line: string) => line.trim());
    if (tleLines.length >= 3) {
      tleMeta = {
        source: 'custom_tle',
        line1: tleLines[1],
        line2: tleLines[2],
      };
    } else if (tleLines.length >= 2) {
      tleMeta = {
        source: 'custom_tle',
        line1: tleLines[0],
        line2: tleLines[1],
      };
    }
  }

  return {
    id:                    sat.id,
    name:                  sat.name,
    dot:                   'ok',
    active:                true,
    simulationGroupId:     groupId,
    orbitElements:         toOrbitElements(sat.orbitParameters ?? {}, orbitMeta.orbitType, orbitMeta.ltan, orbitMeta.epoch),
    orbitMeta,
    orbitColour:           sat.orbitColour ?? sat.orbitColor ?? sat.config?.orbitColour ?? sat.config?.orbitColor ?? config.baseConfig?.orbitColour ?? config.baseConfig?.orbitColor ?? null,
    tleMeta,
    sensors:               (sat.sensors ?? config.sensors ?? []).map(toSensorData),
    physicalCharacteristics: physicalCharacteristics
      ? toPhysicalCharacteristics(physicalCharacteristics)
      : undefined,
    ephemeris,
    propagationStatus:     resolvePropagationStatus(sat, ephemeris),
    orbitPointStatus:      toOrbitStatusMeta(sat, { groupId, satelliteId: sat.id }),
    effectivePerturbationForces: sat.effectivePerturbationForces ?? sat.perturbationForces,
    createdAt: sat.createdAt ?? sat.created_at ?? sat.createdTime ?? sat.created_time ?? null,
  };
}

export function mapApiSatToTreeItem(item: any): TreeSatItem {
  const orbitMeta = toOrbitMeta(item);
  const groupId = getBackendGroupId(item);
  const createdAt = item.createdAt ?? item.created_at ?? item.createdTime ?? item.created_time ?? null;

  if (item.type === 'Group') {
    const children = (item.satellites ?? []).map((sat: any) => mapApiSatChild(sat, orbitMeta, groupId));
    const total = item.satellitesTotal ?? children.length;
    const completedChildren = children.filter(child => child.orbitPointStatus?.status === 'completed').length;
    const failedChildren = children.filter(child => child.orbitPointStatus?.status === 'failed').length;
    const baseStatus = toOrbitStatusMeta(item, {
      groupId,
      satellitesTotal: total,
      satellitesDone: item.satellitesDone,
    });
    const derivedStatus = baseStatus
      ? {
        ...baseStatus,
        satellitesTotal: total,
        satellitesDone: baseStatus.status === 'completed'
          ? total
          : Math.max(baseStatus.satellitesDone ?? 0, completedChildren),
      }
      : total > 0 && completedChildren === total
        ? { status: 'completed' as const, groupId, satellitesTotal: total, satellitesDone: total }
        : failedChildren > 0
          ? { status: 'failed' as const, groupId, satellitesTotal: total, satellitesDone: completedChildren }
          : undefined;

    return {
      type:     'group',
      id:       groupId,
      name:     item.name,
      dot:      'ok',
      expanded: true,
      active:   true,
      orbitPointStatus: derivedStatus,
      orbitColour: item.orbitColour ?? item.orbitColor ?? item.config?.orbitColour ?? item.config?.orbitColor ?? children?.[0]?.orbitColour ?? null,
      groupProperties: item.groupProperties,
      children,
      createdAt: createdAt ?? children?.[0]?.createdAt ?? null,
    } as SatGroup;
  }

  const sat0 = item.satellites?.[0];
  const satellite = mapApiSatChild(
    sat0 ?? { id: item.satelliteId ?? item.satid ?? item.id, name: item.name, orbitParameters: item.referenceOrbit, createdAt },
    orbitMeta,
    groupId,
  );
  if (createdAt && !satellite.createdAt) {
    satellite.createdAt = createdAt;
  }
  if (!satellite.orbitPointStatus) {
    satellite.orbitPointStatus = toOrbitStatusMeta(item, {
      groupId,
      satelliteId: satellite.id,
      satellitesTotal: item.satellitesTotal ?? 1,
      satellitesDone: item.satellitesDone,
    });
  }
  return satellite;
}

export function mapOrbitReportToSatelliteUpdates(report: any, runId?: string, updatedAt?: string): Record<string, Partial<Satellite>> {
  const groupId = report?.group_id ?? report?.groupId;
  const rows = Array.isArray(report?.orbit_report) ? report.orbit_report : [];
  const updates: Record<string, Partial<Satellite>> = {};

  rows.forEach((row: any) => {
    const satelliteId = row?.satelliteId ?? row?.satellite_id;
    const orbitPoints = row?.orbit_points ?? row?.orbitPoints;
    if (!satelliteId || !Array.isArray(orbitPoints) || orbitPoints.length < 8) return;
    const stride = Number(row.pointStride ?? report.pointStride ?? 4);
    if (!Number.isFinite(stride) || stride < 3 || orbitPoints.length < stride * 2) return;
    const numericPoints = orbitPoints.map(Number);
    if (numericPoints.some(value => !Number.isFinite(value))) return;

    updates[satelliteId] = {
      backendOrbitTrack: {
        source: 'backend',
        groupId,
        runId,
        orbitPoints: numericPoints,
        pointStride: stride,
        coordinateOrder: row.coordinateOrder ?? report.coordinateOrder ?? ['time_offset_sec', 'longitude', 'latitude', 'altitude_m'],
        startTime: row.startTime ?? report.startTime,
        duration: row.duration,
        fromUtc: row.fromUtc ?? report.fromUtc,
        toUtc: row.toUtc ?? report.toUtc,
        stepSizeSeconds: row.stepSizeSeconds ?? report.stepSizeSeconds,
        updatedAt,
      },
      propagationStatus: 'ready',
    };
  });

  return updates;
}

export function applyOrbitReportUpdatesToTreeItems(
  items: TreeSatItem[],
  reports: any[],
  runId?: string,
  updatedAt?: string,
): TreeSatItem[] {
  const updatesById = reports.reduce((acc: Record<string, Partial<Satellite>>, report: any) => ({
    ...acc,
    ...mapOrbitReportToSatelliteUpdates(report, runId, updatedAt),
  }), {});

  if (Object.keys(updatesById).length === 0) return items;

  return items.map(item => {
    if ((item as SatGroup).type === 'group') {
      const group = item as SatGroup;
      return {
        ...group,
        children: group.children.map(child => (
          updatesById[child.id] ? { ...child, ...updatesById[child.id] } : child
        )),
      };
    }

    const sat = item as Satellite;
    return updatesById[sat.id] ? { ...sat, ...updatesById[sat.id] } : sat;
  });
}
